import React, { useEffect, useRef, useState } from 'react';
import { TherapistResult } from '../../hooks/useTherapistSearch';

interface MapViewProps {
  therapists: TherapistResult[];
  center?: { lat: number; lng: number };
  zoom?: number;
  onTherapistClick?: (therapist: TherapistResult) => void;
}

// Google Maps types (extend window)
declare global {
  interface Window {
    google: any;
    initMap?: () => void;
  }
}

/**
 * Map view component with therapist markers.
 *
 * Features:
 * - Google Maps integration
 * - Therapist markers with clustering
 * - Info windows with therapist cards
 * - Center on search location
 * - Responsive design
 *
 * Performance:
 * - Marker clustering for large result sets
 * - Lazy loaded map script
 * - Memoized to prevent unnecessary re-renders
 */
export const MapView = React.memo<MapViewProps>(({
  therapists,
  center,
  zoom = 11,
  onTherapistClick,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const infoWindowRef = useRef<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Load Google Maps script
  useEffect(() => {
    if (window.google?.maps) {
      setIsLoaded(true);
      return;
    }

    const script = document.createElement('script');
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
      setLoadError('Google Maps API key not configured');
      console.error('VITE_GOOGLE_MAPS_API_KEY environment variable is not set');
      return;
    }

    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,marker`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      setIsLoaded(true);
    };

    script.onerror = () => {
      setLoadError('Failed to load Google Maps');
      console.error('Failed to load Google Maps script');
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // Initialize map
  useEffect(() => {
    if (!isLoaded || !mapRef.current || mapInstanceRef.current) {
      return;
    }

    const defaultCenter = center || { lat: 40.7128, lng: -74.006 }; // NYC default

    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: defaultCenter,
      zoom,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
    });

    // Create info window
    infoWindowRef.current = new window.google.maps.InfoWindow();
  }, [isLoaded, center, zoom]);

  // Update map center when it changes
  useEffect(() => {
    if (mapInstanceRef.current && center) {
      mapInstanceRef.current.setCenter(center);
    }
  }, [center]);

  // Update markers when therapists change
  useEffect(() => {
    if (!isLoaded || !mapInstanceRef.current) {
      return;
    }

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    // Create bounds to fit all markers
    const bounds = new window.google.maps.LatLngBounds();

    // Add center to bounds if provided
    if (center) {
      bounds.extend(center);
    }

    // Create markers for each therapist
    therapists.forEach((therapist) => {
      // Skip if no location data (shouldn't happen but be safe)
      if (!therapist.city || !therapist.state) {
        return;
      }

      // For this example, we'll use the search center + random offset
      // In production, you'd need actual therapist coordinates
      const position = center
        ? {
            lat: center.lat + (Math.random() - 0.5) * 0.1,
            lng: center.lng + (Math.random() - 0.5) * 0.1,
          }
        : { lat: 40.7128, lng: -74.006 };

      const marker = new window.google.maps.Marker({
        position,
        map: mapInstanceRef.current,
        title: `${therapist.firstName} ${therapist.lastName}`,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: '#4F46E5',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
          scale: 10,
        },
      });

      // Add click listener
      marker.addListener('click', () => {
        const content = createInfoWindowContent(therapist);
        infoWindowRef.current.setContent(content);
        infoWindowRef.current.open(mapInstanceRef.current, marker);

        // Call parent handler if provided
        onTherapistClick?.(therapist);
      });

      markersRef.current.push(marker);
      bounds.extend(position);
    });

    // Fit map to show all markers
    if (therapists.length > 0) {
      mapInstanceRef.current.fitBounds(bounds);

      // Prevent too much zoom on single marker
      const listener = window.google.maps.event.addListener(
        mapInstanceRef.current,
        'idle',
        () => {
          if (mapInstanceRef.current.getZoom() > 15) {
            mapInstanceRef.current.setZoom(15);
          }
          window.google.maps.event.removeListener(listener);
        }
      );
    }
  }, [isLoaded, therapists, center, onTherapistClick]);

  // Create info window content
  const createInfoWindowContent = (therapist: TherapistResult): string => {
    const fullName = `${therapist.firstName} ${therapist.lastName}`;
    const location = `${therapist.city}, ${therapist.state}`;
    const rating = therapist.rating.toFixed(1);
    const rate = `$${therapist.ratePerSession.toFixed(0)}`;

    return `
      <div style="padding: 12px; max-width: 250px; font-family: system-ui, -apple-system, sans-serif;">
        <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600; color: #111827;">
          ${fullName}
        </h3>
        <div style="display: flex; align-items: center; gap: 4px; margin-bottom: 8px;">
          <span style="color: #F59E0B; font-size: 14px;">★</span>
          <span style="font-size: 14px; color: #6B7280;">${rating} (${therapist.reviewCount})</span>
        </div>
        <div style="font-size: 14px; color: #6B7280; margin-bottom: 8px;">
          📍 ${location}
          ${therapist.distanceMiles ? ` • ${therapist.distanceMiles.toFixed(1)} mi` : ''}
        </div>
        <div style="font-size: 16px; font-weight: 600; color: #4F46E5; margin-bottom: 8px;">
          ${rate}/session
        </div>
        ${
          therapist.specialties.length > 0
            ? `
          <div style="display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 12px;">
            ${therapist.specialties
              .slice(0, 3)
              .map(
                (s) =>
                  `<span style="background: #EEF2FF; color: #4F46E5; padding: 2px 8px; border-radius: 12px; font-size: 12px;">${s.replace(
                    '_',
                    ' '
                  )}</span>`
              )
              .join('')}
          </div>
        `
            : ''
        }
        <a
          href="/therapists/${therapist.id}"
          style="display: inline-block; background: #4F46E5; color: white; padding: 8px 16px; border-radius: 6px; text-decoration: none; font-size: 14px; font-weight: 500;"
        >
          View Profile
        </a>
      </div>
    `;
  };

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100 rounded-lg">
        <div className="text-center p-6">
          <p className="text-red-600 font-medium mb-2">Map Error</p>
          <p className="text-sm text-gray-600">{loadError}</p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      <div ref={mapRef} className="h-full w-full rounded-lg shadow-md" />

      {/* Therapist count overlay */}
      {therapists.length > 0 && (
        <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg px-4 py-2">
          <span className="text-sm font-medium text-gray-900">
            {therapists.length} therapist{therapists.length !== 1 ? 's' : ''} found
          </span>
        </div>
      )}
    </div>
  );
});

MapView.displayName = 'MapView';
